<template>
	<div class="width-100 height-100 display-flex flex-column flex-align-center page-wrapper">
		<div class="display-flex flex-column onboarding-content-container height-100" :class="{ show: initialized }">
			<div class="display-flex flex-row flex-justify-space-between mb-8">
				<div class="display-flex onboarding-title">Quick start guide</div>
				<div class="display-flex flex-row flex-align-center">
					<OpenInspirationCenterButton v-if="isInspirationCenterEnabled" />
					<AnemicButton v-if="canCreateNew" @click="showWizard" class="new-button ml-6">New Feature</AnemicButton>
				</div>
			</div>
			<div class="pb-9">
				<OnBoardingSection
					:key="`section${i}`"
					:sectionDetails="section"
					:isOpen="isSectionOpen(i)"
					:class="{ 'mt-3': i !== 0 }"
					@mark-step-as-done="markStepDone"
					@section-clicked="() => onItemClick(i)"
					@open-next-section="() => openNextSection(i)"
					v-for="(section, i) in populatedOnBoardingItems"
				/>
			</div>
		</div>
	</div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapActions } from 'vuex';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import { getOnBoardingItems } from '../on-boarding-service.js';
import OnBoardingSection from '@/components/Onboarding/OnBoardingFlow/OnBoardingSection/OnBoardingSection.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import OpenInspirationCenterButton from '@/components/dialogs/IdeaDialog/OpenInspirationCenterButton.vue';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { setShowInspirationCenter } from '@/store/modules/appLayout/actions';
import { userOnBoardingMetadata } from '@/store/modules/auth/getters';
import { isInspirationCenterEnabled } from '@/store/modules/featureFlags/getters';
import { guidedTutorials } from '@/store/modules/onboarding/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import AnalyticsService from '@/utils/AnalyticsService';
export default {
	name: 'OnBoardingFlow',
	components: { OnBoardingSection, OpenInspirationCenterButton },
	computed: {
		...mapGettersShorthand({
			userOnBoardingMetadata,
			guidedTutorials,
			hasAccountPermission,
			isInspirationCenterEnabled,
		}),
		onBoardingItems() {
			return cloneDeep(getOnBoardingItems(this.guidedTutorials));
		},
		populatedOnBoardingItems() {
			return this.onBoardingItems.map((section) => ({
				...section,
				steps: section.steps.map((step) => {
					if (step.subSteps?.length > 0) {
						const populatedSubSteps = step.subSteps.map((subStep) => ({ ...subStep, isCompleted: this.isStepCompleted(subStep) }));
						return { ...step, isCompleted: this.isStepCompleted(step), subSteps: populatedSubSteps };
					} else {
						return { ...step, isCompleted: this.isStepCompleted(step) };
					}
				}),
			}));
		},
		userCompletedStepsIds() {
			return this.userOnBoardingMetadata.completedStepsIds || [];
		},
		canCreateNew() {
			return this.hasAccountPermission(AccountPermissions.CreateWorkflows);
		},
	},
	methods: {
		...mapActions(['markOnBoardingStepAsComplete']),
		...mapActionsShorthand({ setShowInspirationCenter }),
		showInspirationCenter() {
			this.setShowInspirationCenter({ showInspirationCenter: true });
		},
		showWizard() {
			AnalyticsService.trackButtonClicked(AnalyticsService.BUTTONS.CREATE_NEW_FEATURE, {});

			AppDialogManagerService.openCreationWizardDialog({
				initialState: { projectId: null },
			});
		},
		async markStepDone(stepDetails) {
			if (!stepDetails.isCompleted) {
				try {
					const stepIdToMark = stepDetails.id;
					if (!this.stepsIdsWaitingToBeMarkAsComplete.includes(stepIdToMark)) {
						this.stepsIdsWaitingToBeMarkAsComplete = [...this.stepsIdsWaitingToBeMarkAsComplete, stepIdToMark];
						await this.markOnBoardingStepAsComplete({ id: stepIdToMark });
						this.stepsIdsWaitingToBeMarkAsComplete = this.stepsIdsWaitingToBeMarkAsComplete.filter((id) => id !== stepIdToMark);
					}
				} catch {
					this.$toast.error('Something went wrong, please try again later');
				}
			}
		},
		isSectionOpen(index) {
			return index === this.openedSectionIndex;
		},
		onItemClick(index) {
			if (this.isSectionOpen(index)) {
				this.openedSectionIndex = null;
			} else {
				this.openedSectionIndex = index;
			}
		},
		isStepCompleted(step) {
			if (step.subSteps?.length > 0) {
				return this.areAllSubStepsComplete(step.subSteps);
			} else {
				return this.userCompletedStepsIds?.includes(step.id) || this.stepsIdsWaitingToBeMarkAsComplete.includes(step.id);
			}
		},
		areAllSubStepsComplete(subSteps) {
			return subSteps.every((subStep) => this.isStepCompleted(subStep));
		},
		isSectionCompleted(section) {
			return section.steps.every((step) => step.isCompleted);
		},
		getFirstUnCompleteSectionIndex() {
			return this.populatedOnBoardingItems.findIndex((section) => !this.isSectionCompleted(section));
		},
		openNextSection(currentSectionIndex) {
			if (this.populatedOnBoardingItems.length > currentSectionIndex + 1) {
				this.openedSectionIndex = currentSectionIndex + 1;
			} else {
				this.openedSectionIndex = null;
			}
		},
	},
	mounted() {
		setTimeout(() => {
			this.initialized = true;
		}, 10);
		this.openedSectionIndex = this.getFirstUnCompleteSectionIndex();
	},
	data() {
		return {
			initialized: false,
			openedSectionIndex: null,
			stepsIdsWaitingToBeMarkAsComplete: [],
		};
	},
};
</script>

<style scoped lang="scss">
.new-button {
	width: 145px !important;
	padding: 0px !important;
	height: 45px !important;
	border-radius: 8px !important;
	background-color: #6762f0 !important;

	color: var(--bg-color);
	font-size: 14px !important;
	font-weight: normal !important;
}
.onboarding-title {
	font-size: 30px;
	font-weight: bold;
	letter-spacing: 0px;
	color: var(--primary-font-color);
}
.onboarding-content-container {
	opacity: 0;
	height: 100%;
	margin: 76px 0 76px 0;
	&.show {
		opacity: 1;
		transition: 1s;
	}

	max-width: 1120px;
	width: calc(100% - 40px);
}
.page-wrapper {
	overflow-y: scroll;
}
</style>
